/**
 * SearchArtifacts slice — generic, filter-driven artifact search.
 *
 * searchArtifacts(input): POST /artifacts/search via beaconApi; stores the raw
 * SearchArtifacts summaries + total count. Callers supply the
 * SearchArtifactsInput (filters/pagination/sort). The bulk artifact search page
 * uses it with filters.artifactIds = [queried ids].
 *
 * A global search keeps each region's page separate in `regionResults`;
 * `artifacts` is every region's rows flattened, for single-region consumers.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiArtifactSummary, SearchArtifactsInput } from './searchArtifactsApi';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';
import {
  RegionSearchState, SearchFanOutResult, describeFailures, fanOutSearch,
  flattenPages, regionBases, regionSearchInitialState,
} from '../regionFanOut';

export interface SearchArtifactsState extends RegionSearchState<ApiArtifactSummary> {
  /** All regions' rows in one list. */
  artifacts: ApiArtifactSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  currentRequestId: string | null;
  /** Last search input; re-dispatched on region change. */
  lastInput: SearchArtifactsInput | null;
}

export const searchArtifactsInitialState: SearchArtifactsState = {
  artifacts: [],
  total: 0,
  loading: false,
  error: null,
  currentRequestId: null,
  lastInput: null,
  ...regionSearchInitialState,
};

export const searchArtifacts = createAsyncThunk<
  SearchFanOutResult<ApiArtifactSummary>, SearchArtifactsInput,
  { state: RootState; rejectValue: string }
>('searchArtifacts/run', async (input, { getState }) => {
  const run = async (apiBase: string) => {
    const response = await beaconApi.searchArtifacts(input, apiBase);
    return { items: response.artifacts ?? [], total: response.totalArtifactsCount ?? 0 };
  };
  return fanOutSearch(regionBases(getState().env), run);
});

const searchArtifactsSlice = createSlice({
  name: 'searchArtifacts',
  initialState: searchArtifactsInitialState,
  reducers: {
    clearSearchArtifacts: () => searchArtifactsInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchArtifacts.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
        state.lastInput = action.meta.arg;
      })
      .addCase(searchArtifacts.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        const { regionResults, searchedRegions, errors } = action.payload;
        const flat = flattenPages(regionResults);
        state.loading = false;
        state.regionResults = regionResults;
        state.searchedRegions = searchedRegions;
        state.regionErrors = errors;
        state.artifacts = flat.items;
        state.total = flat.total;
        state.error = flat.items.length === 0 && errors.length > 0
          ? `No region returned artifacts. ${describeFailures(errors)}`
          : null;
      })
      .addCase(searchArtifacts.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to search artifacts';
      });
  },
});

export const { clearSearchArtifacts } = searchArtifactsSlice.actions;
export default searchArtifactsSlice.reducer;
